import React, { useState, useEffect, useCallback } from 'react';
import {
  Download, FileText, FileSpreadsheet, FileJson, Music,
  ExternalLink, ShoppingCart, X, CheckCircle2, XCircle,
  Loader2, AlertTriangle, Copy, Check
} from 'lucide-react';
import './ExportModal.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ExportModal = ({ crate, onClose }) => {
  const [activeTab, setActiveTab] = useState('export'); // 'export' or 'purchase'
  const [purchaseLinks, setPurchaseLinks] = useState(null);
  const [isLoadingLinks, setIsLoadingLinks] = useState(false);
  const [isExporting, setIsExporting] = useState(null);
  const [copiedLink, setCopiedLink] = useState(null);

  const getAuthToken = useCallback(() => localStorage.getItem('beatseek_token'), []);

  // Fetch purchase links
  const fetchPurchaseLinks = useCallback(async () => {
    if (purchaseLinks) return;
    
    setIsLoadingLinks(true);
    try {
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_URL}/api/crates/${crate.id}/purchase-links`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPurchaseLinks(data);
      }
    } catch (err) {
      console.error('Error fetching purchase links:', err);
    } finally {
      setIsLoadingLinks(false);
    }
  }, [crate.id, getAuthToken, purchaseLinks]);

  useEffect(() => {
    if (activeTab === 'purchase') {
      fetchPurchaseLinks();
    }
  }, [activeTab, fetchPurchaseLinks]);

  // Handle export
  const handleExport = async (format, verifiedOnly = false) => {
    setIsExporting(format);
    try {
      const token = getAuthToken();
      let url = `${BACKEND_URL}/api/crates/${crate.id}/export`;
      
      if (format === 'm3u') {
        url += `?verified_only=${verifiedOnly}`;
      } else {
        url += `/${format}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const error = await response.json();
        alert(error.detail || 'Export failed');
        return;
      }
      
      // Get filename from header or generate one
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `${crate.name}.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Export error:', err);
      alert('Export failed');
    } finally {
      setIsExporting(null);
    }
  };

  // Copy link to clipboard
  const copyLink = async (url, trackId) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedLink(`${trackId}-${url}`);
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const stats = {
    total: crate.track_count || 0,
    verified: crate.verified_count || 0,
    mock: crate.mock_count || 0,
    ownershipPct: crate.track_count > 0 
      ? Math.round((crate.verified_count / crate.track_count) * 100) 
      : 0
  };

  return (
    <div className="export-modal-overlay" onClick={onClose}>
      <div className="export-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="export-modal-header">
          <h3>Export & Purchase</h3>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Crate Info */}
        <div className="crate-summary">
          <div className="crate-name">{crate.name}</div>
          <div className="crate-stats-row">
            <span className="stat">
              <Music size={14} />
              {stats.total} tracks
            </span>
            <span className="stat verified">
              <CheckCircle2 size={14} />
              {stats.verified} verified
            </span>
            <span className="stat mock">
              <XCircle size={14} />
              {stats.mock} mock
            </span>
          </div>
          
          {/* Ownership Bar */}
          <div className="ownership-progress">
            <div className="progress-label">
              <span>Ownership</span>
              <span>{stats.ownershipPct}%</span>
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ width: `${stats.ownershipPct}%` }}
              />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="export-tabs">
          <button 
            className={`tab ${activeTab === 'export' ? 'active' : ''}`}
            onClick={() => setActiveTab('export')}
          >
            <Download size={16} />
            Export
          </button>
          <button 
            className={`tab ${activeTab === 'purchase' ? 'active' : ''}`}
            onClick={() => setActiveTab('purchase')}
          >
            <ShoppingCart size={16} />
            Purchase Tracks
            {stats.mock > 0 && <span className="badge">{stats.mock}</span>}
          </button>
        </div>

        {/* Content */}
        <div className="export-content">
          {activeTab === 'export' ? (
            <div className="export-options">
              {/* Warning if no verified tracks */}
              {stats.verified === 0 && (
                <div className="export-warning">
                  <AlertTriangle size={16} />
                  <span>No verified tracks. M3U export for DJ software requires owned tracks.</span>
                </div>
              )}

              {/* M3U Export - DJ Software */}
              <div className="export-section">
                <h4>DJ Software (M3U)</h4>
                <p className="section-desc">Export as M3U playlist for Rekordbox, Serato, Traktor</p>
                <div className="export-buttons">
                  <button 
                    className="export-btn primary"
                    onClick={() => handleExport('m3u', true)}
                    disabled={isExporting || stats.verified === 0}
                  >
                    {isExporting === 'm3u' ? <Loader2 size={16} className="spinning" /> : <Download size={16} />}
                    Verified Only ({stats.verified})
                  </button>
                  <button 
                    className="export-btn secondary"
                    onClick={() => handleExport('m3u', false)}
                    disabled={isExporting}
                  >
                    {isExporting === 'm3u' ? <Loader2 size={16} className="spinning" /> : <Download size={16} />}
                    All Tracks (Reference)
                  </button>
                </div>
              </div>

              {/* Text Export */}
              <div className="export-section">
                <h4>Tracklist (Text)</h4>
                <p className="section-desc">Plain text list with BPM, key, and transition notes</p>
                <button 
                  className="export-btn"
                  onClick={() => handleExport('text')}
                  disabled={isExporting}
                >
                  {isExporting === 'text' ? <Loader2 size={16} className="spinning" /> : <FileText size={16} />}
                  Export .txt
                </button>
              </div>

              {/* CSV Export */}
              <div className="export-section">
                <h4>Spreadsheet (CSV)</h4>
                <p className="section-desc">Import into Excel, Google Sheets, or databases</p>
                <button 
                  className="export-btn"
                  onClick={() => handleExport('csv')}
                  disabled={isExporting}
                >
                  {isExporting === 'csv' ? <Loader2 size={16} className="spinning" /> : <FileSpreadsheet size={16} />}
                  Export .csv
                </button>
              </div>

              {/* JSON Export */}
              <div className="export-section">
                <h4>Backup (JSON)</h4>
                <p className="section-desc">Full crate data for backup or API use</p>
                <button 
                  className="export-btn"
                  onClick={() => handleExport('json')}
                  disabled={isExporting}
                >
                  {isExporting === 'json' ? <Loader2 size={16} className="spinning" /> : <FileJson size={16} />}
                  Export .json
                </button>
              </div>
            </div>
          ) : (
            <div className="purchase-options">
              {isLoadingLinks ? (
                <div className="loading-links">
                  <Loader2 size={24} className="spinning" />
                  <span>Finding purchase links...</span>
                </div>
              ) : stats.mock === 0 ? (
                <div className="all-verified">
                  <CheckCircle2 size={40} />
                  <h4>All tracks verified!</h4>
                  <p>Your crate is ready for DJ software export.</p>
                </div>
              ) : purchaseLinks?.purchase_suggestions?.length > 0 ? (
                <div className="purchase-list">
                  <p className="purchase-intro">
                    Purchase these tracks to enable M3U export for your DJ software
                  </p>
                  {purchaseLinks.purchase_suggestions.map((track, idx) => (
                    <div key={track.track_id || idx} className="purchase-track">
                      <div className="track-info">
                        <span className="track-name">{track.title}</span>
                        <span className="track-artist">{track.artist}</span>
                      </div>
                      <div className="purchase-links">
                        {Object.entries(track.purchase_links).map(([store, url]) => (
                          <a
                            key={store}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`store-link ${store}`}
                            title={`Search on ${store}`}
                          >
                            {store === 'beatport' && 'BP'}
                            {store === 'traxsource' && 'TX'}
                            {store === 'bandcamp' && 'BC'}
                            {store === 'juno' && 'JD'}
                          </a>
                        ))}
                        <button
                          className="copy-btn"
                          onClick={() => copyLink(track.purchase_links.beatport, track.track_id)}
                          title="Copy Beatport link"
                        >
                          {copiedLink === `${track.track_id}-${track.purchase_links.beatport}` 
                            ? <Check size={14} /> 
                            : <Copy size={14} />
                          }
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-links">
                  <p>No purchase links available</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
